import React, { ReactElement, useState, useRef, useEffect } from "react"
import styled from "styled-components"
import { COLOR } from "utils/color"

interface Option {
  value: string
  text: string
}

interface StyledSelectProps {
  options: Option[]
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  required?: boolean
  width?: string
  height?: string
  disabled?: boolean
  clearable?: boolean
  fontSize?: number
}

const SelectContainer = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  width: ${props => props.width || "100%"};
  position: relative;
`

const Label = styled.label`
  font-size: 10px;
  color: #2c3e50;
  margin-bottom: 2px;
  display: block;
`

const SelectButton = styled.button<{ height?: string; isOpen: boolean }>`
  background-color: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  padding: 10px 12px;
  height: ${props => props.height || "auto"};
  transition: all 0.2s ease;
  outline: none;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  color: #333;

  &:focus {
    border-color: #3498db;
  }

  &:hover:not(:focus) {
    border-color: #c1c7cd;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .placeholder {
    color: #c7c7c7;
  }

  .arrow {
    margin-left: 8px;
    font-size: 12px;
    transition: transform 0.2s ease;
    transform: ${props => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  }
`

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  display: ${props => (props.isOpen ? "block" : "none")};
`

const DropdownItem = styled.div<{ isSelected: boolean }>`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  background-color: ${props => (props.isSelected ? "#f8f9fa" : "white")};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f1f3f4;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #666;
  }
`

export default function StyledSelect({
  options,
  value = "",
  onChange,
  placeholder = "Select an option",
  label,
  required = false,
  width,
  height,
  disabled = false,
  clearable = false,
  fontSize,
}: StyledSelectProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.value === value)
  const displayText = selectedOption ? selectedOption.text : placeholder

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange("")
  }

  return (
    <SelectContainer width={width} ref={selectRef}>
      {label && (
        <Label>
          {label}
          {required && <span style={{ color: "#e74c3c" }}>*</span>}
        </Label>
      )}
      <SelectButton
        type="button"
        height={height}
        isOpen={isOpen}
        onClick={handleToggle}
        disabled={disabled}
        style={{ fontSize: fontSize || 14 }}
      >
        <span className={selectedOption ? "" : "placeholder"}>{displayText}</span>
        <div style={{ display: "flex", alignItems: "center" }}>
          {clearable && value && (
            <ClearButton onClick={handleClear} type="button">
              ×
            </ClearButton>
          )}
          <span className="arrow" style={{ fontSize: 8, color: COLOR.LIGHT_GREY }}>
            ▼
          </span>
        </div>
      </SelectButton>

      <DropdownMenu isOpen={isOpen}>
        {options.map(option => (
          <DropdownItem
            key={option.value}
            isSelected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            {option.text}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </SelectContainer>
  )
}
